import { type Ref, computed, nextTick, ref, watch } from 'vue'
import type {
  InventoryFilter,
  InventoryMapActionType,
  InventoryMapObjectData,
  InventoryMapObjectTypes,
} from './types.ts'
import { INVENTORY_MAP_NO_BIND_DEPENDENCY } from './constants.ts'
import type { MapComponentExpose } from './adapters/types.ts'
import { cloneDeep, debounce } from 'lodash'
import { useLoadInventObject, useLoadInventSquareCoords } from './api.ts'
import { bboxPolygon, booleanIntersects, featureCollection, nearestPoint, point } from '@turf/turf'
import { InventObject } from './classes/InventObject.ts'
import { InventObjectCreator } from './classes/InventObjectCreator.ts'
import { useNotification } from '@/shared/ui'
import { useTranslate } from '@/shared/lib'
import { useAuth } from '@/entities/Auth'
import type { Feature } from 'ymaps3'
import {
  DEFAULT_MAP_BOUNDS,
  getBoundsFitLocation,
  waitForMapContainerSize,
  canCreateMapObjectWithoutBindParent,
  resolveMapBindParentTypeIds,
} from './helpers.ts'

export const useModel = (
  map: Ref<MapComponentExpose | undefined>,
  mapObjectId: Ref<number>,
  options?: { skipInitialBoundsFit?: Ref<boolean> },
) => {
  const { addError, addSuccess } = useNotification()
  const { trs } = useTranslate()

  const center = ref<number[]>([30.3351, 59.9343])
  const zoom = ref<number>(18)
  const bounds = ref<number[][]>([])

  const isProject = ref(false)
  const action = ref<InventoryMapActionType>('select')
  const addObjectType = ref<InventoryMapObjectTypes | null>(null)

  const data = ref<InventoryMapObjectData[]>([])
  const objectsList = ref<InventObject[]>([])
  const inventObjectTypes = ref<InventoryMapObjectTypes[]>([])

  const selectedObjects = ref<number[]>([])

  /** Ид объектов которые используются для центрирования и выборки из урл */
  const preSelectedObjectIds = ref<string>('')

  /**
   * Функция загрузки списка инвентаризационных объектов
   * @param objectId
   */
  const { send, data: objectDataList, isPending } = useLoadInventObject()

  /** Only the latest viewport load may surface errors (avoids toast spam while zooming). */
  let viewportLoadSeq = 0
  let pendingFilterApply = false
  let isFilterWatchReady = false
  const isFilterReloading = ref(false)

  const isMapDataLoading = computed(() => isPending.value || isFilterReloading.value)

  const loadData = async (objectId: Ref<number>, currentFilter?: InventoryFilter) => {
    if (!map.value) return

    const seq = ++viewportLoadSeq

    const [[minLon, minLat], [maxLon, maxLat]] = map.value.entity.bounds as [
      [number, number],
      [number, number],
    ]

    const area = [
      [minLon, minLat],
      [minLon, maxLat],
      [maxLon, maxLat],
      [maxLon, minLat],
    ]

    try {
      await send({
        object_id: objectId.value,
        points: area,
        zoom: Math.round(zoom.value),
        type_ids: currentFilter?.types ?? [],
        field_filters: currentFilter?.fieldFilters ?? [],
      })

      if (seq !== viewportLoadSeq) return

      if (pendingFilterApply) {
        pendingFilterApply = false
        isFilterReloading.value = false
        addSuccess(trs('itf.map_filter_applied'))
      }
    } catch (error: any) {
      if (seq !== viewportLoadSeq) return

      if (pendingFilterApply) {
        pendingFilterApply = false
        isFilterReloading.value = false
      }

      addError(
        trs(
          error?.response?.data?.data?.msg ||
            error?.response?.data?.error ||
            'request.unknown_error',
        ),
      )
      throw error
    }
  }

  watch(objectDataList, () => {
    if (!objectDataList.value || !map.value) return

    const payload = objectDataList.value as { data?: unknown }
    const list = Array.isArray(payload) ? payload : payload?.data
    if (Array.isArray(list)) {
      data.value = list as InventoryMapObjectData[]
    }

    if (map.value?.entity?.center) {
      center.value = map.value.entity.center
    }
  })

  /**
   * Функция загрузки данных центра карты
   */
  const loadCenterMap = () => {
    const result = useLoadInventSquareCoords(mapObjectId, preSelectedObjectIds)

    /**
     * Дефолтная подгонка без `ids` должна выполниться один раз для объекта карты.
     * Подгонка по `ids` выполняется для каждого нового набора id из URL.
     */
    let defaultFitKey = ''
    let fittedPreSelectedKey = ''
    let lastPreSelectedObjectKey = ''
    let fitSeq = 0

    watch(
      [result.data, map, preSelectedObjectIds, result.isFetching],
      async () => {
        const seq = ++fitSeq
        await nextTick()
        if (seq !== fitSeq) return
        if (!map.value) return
        if (options?.skipInitialBoundsFit?.value) return

        /**
         * Источник границ: при наличии предвыбранных `ids` фитим по прямоугольнику
         * с сервера (ждём ответ запроса), иначе — по дефолтным границам региона.
         */
        const objectKey = String(mapObjectId.value)
        const ids = preSelectedObjectIds.value
        let coords = DEFAULT_MAP_BOUNDS

        if (ids) {
          const idsFitKey = `${objectKey}:${ids}`
          if (fittedPreSelectedKey === idsFitKey) return
          if (result.isFetching.value) return
          if (!result.data.value) return

          coords = result.data.value.data
        } else {
          const nextDefaultFitKey = `${objectKey}:default`
          if (defaultFitKey === nextDefaultFitKey) return
          if (lastPreSelectedObjectKey === objectKey) return
        }

        if (
          Number.isFinite(coords.min_lon) &&
          Number.isFinite(coords.min_lat) &&
          Number.isFinite(coords.max_lon) &&
          Number.isFinite(coords.max_lat)
        ) {
          /**
           * На перезагрузке карта появляется в DOM сразу после прелоадера, и без
           * ожидания layout-а размеры контейнера ещё нулевые — фит считался бы по
           * размеру окна и обрезал крайние объекты.
           */
          await waitForMapContainerSize()
          if (seq !== fitSeq) return
          if (!map.value) return

          const location = getBoundsFitLocation(coords, { fitWidth: !!ids })

          map.value.entity.setLocation(location)

          zoom.value = location.zoom
          center.value = location.center

          if (ids) {
            fittedPreSelectedKey = `${objectKey}:${ids}`
            lastPreSelectedObjectKey = objectKey
          } else {
            defaultFitKey = `${objectKey}:default`
          }
        }
      },
      { immediate: true },
    )

    /**
     * Прелоадер показываем только пока реально ждём ответ сервера (есть `ids`).
     * Без `ids` запрос отключён, поэтому vue-query навсегда остаётся в статусе
     * `pending` — в этом случае загрузки нет, фит делается по дефолтным границам.
     */
    const isPending = computed(() => !!preSelectedObjectIds.value && result.isPending.value)

    return { isPending }
  }

  /* Следим за списком объектов и обновляем список элементов */
  watch(data, () => {
    objectsList.value = formatingData(data.value)
  })

  /* Следим за изменением action и сбрасываем addObjectType */
  watch(action, () => {
    addObjectType.value = null
  })

  const { hasElementPermission } = useAuth()

  /**
   * Функция форматирования объектов для использования на карте
   * @param objects
   */
  const formatingData = (objects: InventoryMapObjectData[]) => {
    const result: InventObject[] = []

    objects.forEach((item) => {
      try {
        const inventObject = InventObjectCreator.create(item, item.object_invent_type)
        result.push(inventObject)
      } catch (e) {
        console.warn(`Skipping object ${item.id} (type: ${item.object_type?.object_name}): ${e}`)
      }
    })

    return result.filter((item) => hasElementPermission(item.getObjectTypeId(), 'read'))
  }

  /** Функции добавления объекта в список выбранных **/

  /** Функция проверки объекта на выделение */
  const isSelected = (object: InventObject) => {
    return selectedObjects.value.includes(object.getId())
  }

  /**
   * Функция добавления одного объекта в новый список
   * @param object
   */
  const selectObject = (object: InventObject) => {
    selectedObjects.value = [object.getId()]
  }

  /**
   * Функция добавления одного объекта в существующий список
   * @param object
   */
  const selectAddObject = (object: InventObject) => {
    const id = object.getId()

    if (isSelected(object)) {
      selectedObjects.value = selectedObjects.value.filter((item) => item !== id)
    } else {
      selectedObjects.value.push(id)
    }
  }

  /**
   * Функция очистки выделенных объектов
   */
  const selectedClear = () => {
    selectedObjects.value = []
    editSelectObject.value = null
  }

  /**
   * Функция выделения объектов при выделении области
   * @param coords
   */
  const selectedHandler = (coords: number[][]) => {
    const bbox: any = [
      Math.min(coords[0][0], coords[1][0]),
      Math.min(coords[0][1], coords[1][1]),
      Math.max(coords[0][0], coords[1][0]),
      Math.max(coords[0][1], coords[1][1]),
    ]

    const area = bboxPolygon(bbox)

    const objectSelected = objectsList.value.filter((object) => {
      return booleanIntersects(object.getFeature(), area)
    })

    objectSelected.forEach((item) => selectAddObject(item as InventObject))
  }

  /** Отфильтрованные объекты **/
  const filtered = ref<InventObject[]>([])
  const filter = ref<InventoryFilter>({
    types: [],
    fields: [],
    fieldFilters: [],
  })
  const filterTypeIdsKey = computed(() => filter.value.types.join(','))
  const filterRequestKey = computed(() =>
    JSON.stringify({
      types: filter.value.types,
      fieldFilters: filter.value.fieldFilters,
    }),
  )

  /**
   * Следим за изменением фильтра типов отображаемых полей
   * и сохраняем выбор пользователя
   **/
  watch(
    () => filter.value.fields,
    debounce((value) => {
      const fields = JSON.parse(localStorage.getItem('map_filter_fields') || '{}')
      fields[mapObjectId.value] = value

      localStorage.setItem('map_filter_fields', JSON.stringify(fields))
    }, 300),
  )

  /** Наблюдаем за изменениями и используем фильтры **/
  watch(
    [objectsList, filterTypeIdsKey],
    ([newList], [oldList]) => {
      const objectListHadLength = !!(oldList as InventObject[])?.length
      const objectListHasLength = !!(newList as InventObject[]).length

      if (!objectListHadLength && !objectListHasLength) return

      if (!objectListHadLength && objectListHasLength) {
        filtered.value = cloneDeep(newList as InventObject[])
      } else if (objectListHasLength && !filter.value.types.length) {
        filtered.value = cloneDeep(newList as InventObject[])
      } else {
        const allObjectTypeIds = (newList as InventObject[]).map((i) => i.getSubType().id)
        const filterTypeIds = filter.value.types
          .map((id) => (newList as InventObject[]).find((o) => o.getId() === id)?.getSubType().id)
          .filter(Boolean)
        const combinedTypeIds = [...new Set([...allObjectTypeIds, ...filterTypeIds])]
        filtered.value = (newList as InventObject[]).filter((item) => {
          return combinedTypeIds.includes(item.getSubType().id)
        })
      }
    },
    { immediate: true },
  )

  /** Следим за типом выбранного объекта и обновляем данные **/
  watch(
    mapObjectId,
    () => {
      /* Сбрасываем выбранные объекты */
      selectedObjects.value = []
    },
    { immediate: true },
  )

  /**
   * Функция нахождения ближайшей связанной точки
   * @param coordinates
   */
  const getNearestRootObjectId = (coordinates: any[]) => {
    if (
      !addObjectType.value ||
      !addObjectType.value?.properties.is_bind ||
      addObjectType.value?.dependency === INVENTORY_MAP_NO_BIND_DEPENDENCY
    )
      return Number(mapObjectId.value)

    const parentTypeIds = resolveMapBindParentTypeIds(addObjectType.value)
    const rootOfTypeObjects = objectsList.value.filter((item) =>
      parentTypeIds.includes(item.getObjectTypeId()),
    )

    if (!rootOfTypeObjects.length) {
      if (canCreateMapObjectWithoutBindParent(addObjectType.value.functional_type)) {
        return Number(mapObjectId.value)
      }
      addError(trs('validate.not_root_object'))
      throw new Error(trs('validate.not_root_object'))
    }

    const points: any = featureCollection([])

    rootOfTypeObjects.forEach((item) => {
      item.coordinates.forEach((coords: any) => {
        points.features.push(point(coords, { id: item.getId() }))
      })
    })

    const targetPoint = point(coordinates[0])
    const rootPoint = nearestPoint(targetPoint, points)

    return rootPoint.properties.id
  }

  const editSelectObject = ref<InventObject | null>(null)
  const editNewPoint = ref<any | Feature>(null)
  const isEditNewPoint = ref<boolean>(false)

  /** После перезагрузки данных синхронизируем editSelectObject с новым экземпляром из filtered **/
  watch(filtered, () => {
    if (!editSelectObject.value) return
    const id = editSelectObject.value.getId()
    const match = filtered.value.find((o) => o.getId() === id)
    if (match && match !== editSelectObject.value) {
      editSelectObject.value = match
    }
  })

  /**
   * Функция задания значения отображения добавочной точки
   * @param value
   */
  const setIsEditNewPoint = (value: boolean) => {
    isEditNewPoint.value = value
    editNewPoint.value = null
  }

  const loadDataFiltered = (objectId: Ref<number>) => {
    return loadData(objectId, filter.value)
  }

  /** Debounced reload while panning/zooming — avoids parallel polygon requests and error toasts */
  const debouncedReload = debounce((objectId: Ref<number>) => {
    void loadDataFiltered(objectId).catch(() => {})
  }, 400)

  /** Reload data when map filters change */
  watch(filterRequestKey, () => {
    debouncedReload(mapObjectId)

    if (!isFilterWatchReady) {
      isFilterWatchReady = true
      return
    }

    pendingFilterApply = true
    isFilterReloading.value = true
  })

  return {
    center,
    zoom,
    bounds,
    isProject,
    action,
    addObjectType,
    inventObjectTypes,
    objectsList,
    data,
    selectedObjects,
    filtered,
    filter,
    editSelectObject,
    editNewPoint,
    isEditNewPoint,
    preSelectedObjectIds,

    loadData,
    loadDataFiltered,
    debouncedReload,
    selectObject,
    selectAddObject,
    isSelected,
    selectedClear,
    selectedHandler,
    getNearestRootObjectId,
    setIsEditNewPoint,
    loadCenterMap,
    isMapDataLoading,
  }
}
